import { XCloseIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { getColorFromTag, getTagIcon } from '@/lib/tags'
import { Tooltip, TooltipTrigger } from 'react-aria-components'

export interface TagChipProps {
  label: string
  tagId?: string // Original tag ID for color generation
  selected?: boolean
  onClick?: () => void
  onRemove?: () => void
  size?: 'sm' | 'md'
  variant?: 'default' | 'outline' | 'colored'
  iconOnly?: boolean // Show only icon with label on hover
  className?: string
}

export function TagChip({
  label,
  tagId,
  selected = false,
  onClick,
  onRemove,
  size = 'md',
  variant = 'default',
  iconOnly = false,
  className,
}: TagChipProps) {
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
  }

  const iconOnlySizeClasses = {
    sm: 'text-sm px-1.5 py-0.5',
    md: 'text-base px-2 py-1',
  }

  // For colored variant, generate color from tagId or label
  const coloredStyle =
    variant === 'colored'
      ? (() => {
          const baseColor = getColorFromTag(tagId || label)
          // Create darker text color (0.7 multiplier)
          const rgb = baseColor.match(/[A-Za-z0-9]{2}/g)
          if (rgb) {
            const [r, g, b] = rgb.map((c) => Math.floor(parseInt(c, 16) * 0.7))
            const textColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
            return {
              backgroundColor: `${baseColor}40`, // 25% opacity
              borderColor: `${baseColor}45`,
              color: textColor,
            }
          }
          return {}
        })()
      : {}

  const icon = iconOnly ? getTagIcon(tagId || '') : null

  // For iconOnly mode, use a subtle border without background
  const iconOnlyStyle = iconOnly
    ? {
        backgroundColor: 'transparent',
        borderColor: '#e5e7eb', // gray-200
      }
    : {}

  const chipContent = (
    <span
      onClick={onClick}
      style={iconOnly ? iconOnlyStyle : (variant === 'colored' ? coloredStyle : undefined)}
      className={cn(
        'inline-flex items-center gap-1 rounded-lg font-bold transition-colors border-[1.5px]',
        iconOnly ? iconOnlySizeClasses[size] : sizeClasses[size],
        iconOnly && [
          'bg-transparent hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800',
          onClick && 'cursor-pointer',
        ],
        !iconOnly && variant === 'default' && [
          selected
            ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
          onClick && 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700',
          selected && onClick && 'hover:bg-gray-800 dark:hover:bg-gray-200',
          'border-transparent',
        ],
        !iconOnly && variant === 'outline' && [
          selected
            ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
            : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-400',
          onClick && 'cursor-pointer hover:border-brand-500 hover:bg-brand-50',
        ],
        !iconOnly && variant === 'colored' && [
          onClick && 'cursor-pointer hover:opacity-80',
        ],
        className
      )}
    >
      {iconOnly ? icon : label}
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
        >
          <XCloseIcon className="h-3 w-3" />
        </button>
      )}
    </span>
  )

  // Wrap in tooltip when iconOnly mode is enabled
  if (iconOnly) {
    return (
      <TooltipTrigger delay={200}>
        {chipContent}
        <Tooltip
          className={cn(
            'rounded-md bg-gray-900 px-2 py-1 text-xs text-white shadow-lg',
            'dark:bg-gray-100 dark:text-gray-900',
            'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
            'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95'
          )}
        >
          {label}
        </Tooltip>
      </TooltipTrigger>
    )
  }

  return chipContent
}

export default TagChip
